import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Building2, CheckCircle2, Ban, RotateCcw, LogIn, ExternalLink, Car, Inbox, Trash2, Sparkles, Lock, Headset, Search, MapPin, Server, Radar, AlertTriangle, CloudOff, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ADDON_STATUS } from "@/lib/packages";
import { BRAND } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { VinconexLogo } from "@/components/VinconexMark";
import { DemandNudgePanel } from "@/components/DemandNudgePanel";
import { PlatformAdminsPanel } from "@/components/PlatformAdminsPanel";
import { ContactInboxPanel } from "@/components/ContactInboxPanel";
import { SecurityPanel } from "@/components/SecurityPanel";
import { AuditLogPanel } from "@/components/AuditLogPanel";
import { DealerOnboardingPanel } from "@/components/DealerOnboardingPanel";

const STATUS = {
  active: "border-emerald-700/50 bg-emerald-950/40 text-emerald-400",
  pending: "border-amber-700/50 bg-amber-950/40 text-amber-400",
  suspended: "border-red-700/50 bg-red-950/40 text-red-400",
  declined: "border-slate-600 bg-slate-900 text-slate-400",
};

function Stat({ label, value, testId, alert }) {
  return (
    <div className={`rounded-xl border p-4 ${alert ? "border-red-900/60 bg-red-950/20" : "border-[#2A2E3D] bg-[#12141C]"}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech">{label}</div>
      <div className={`font-heading text-2xl font-black mt-1 ${alert ? "text-red-400" : ""}`}>{value}</div>
    </div>
  );
}

// Which inventory platform a dealer's website runs — green = a real feed we fully support.
const PLATFORMS = {
  dealer_com: { label: "Dealer.com", cls: "border-emerald-700/50 bg-emerald-950/40 text-emerald-400", hint: "Dealer.com (DDC) inventory API — fully supported: full lot, pricing, photos, lot dates, CARFAX links" },
  ridemotive: { label: "RideMotive", cls: "border-emerald-700/50 bg-emerald-950/40 text-emerald-400", hint: "RideMotive inventory feed — fully supported: full lot, pricing, photos, lot dates, website location" },
  crawl: { label: "Other · page crawl", cls: "border-amber-700/50 bg-amber-950/40 text-amber-400", hint: "No known inventory feed — Vinconex crawls the sitemap page by page (slower, fewer fields)" },
  unknown: { label: "No feed found", cls: "border-red-900/60 bg-red-950/30 text-red-400", hint: "Neither an inventory feed nor vehicle listings in the sitemap — this site needs a look before it can sync" },
  none: { label: "Not detected", cls: "border-slate-700 bg-slate-900 text-slate-400", hint: "Not checked yet — press Detect, or it fills in on the first sync" },
  demo: { label: "Demo store", cls: "border-[#0A8CF2]/50 bg-[#0A8CF2]/10 text-[#7FC3FF]", hint: "Vinconex demo store — hand-built inventory for product videos, never synced from a real website" },
};

function PlatformBadge({ d, busy, onDetect }) {
  const p = PLATFORMS[d.inventory_platform] || PLATFORMS.none;
  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant="outline" title={`${p.hint}${d.inventory_platform_detail ? ` · ${d.inventory_platform_detail}` : ""}`} data-testid={`platform-source-${d.id}`} className={`${p.cls} text-[10px] font-mono-tech flex items-center gap-1`}>
        <Server className="h-2.5 w-2.5" /> {p.label}
      </Badge>
      <button type="button" onClick={onDetect} disabled={busy} title="Check which inventory platform this website runs" data-testid={`platform-detect-${d.id}`} className="text-slate-500 hover:text-[#7FC3FF] disabled:opacity-50">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Radar className="h-3 w-3" />}
      </button>
    </span>
  );
}

function SyncHealthBadge({ d }) {
  if (d.sync_fail_streak > 0) {
    return (
      <Badge variant="outline" title={d.sync_failure_reason || d.sync_message || "The last inventory sync failed"} data-testid={`platform-sync-failing-${d.id}`} className="border-red-900/60 bg-red-950/40 text-red-400 text-[10px] font-mono-tech flex items-center gap-1">
        <AlertTriangle className="h-2.5 w-2.5" /> Sync failing ×{d.sync_fail_streak}
      </Badge>
    );
  }
  if (!d.last_synced_at) {
    return (
      <Badge variant="outline" title="This store has never completed an inventory sync" data-testid={`platform-sync-never-${d.id}`} className="border-amber-700/50 bg-amber-950/40 text-amber-400 text-[10px] font-mono-tech flex items-center gap-1">
        <CloudOff className="h-2.5 w-2.5" /> Never synced
      </Badge>
    );
  }
  return null;
}

function locationOf(d) {
  const cityState = [d.city, d.state].filter(Boolean).join(", ");
  return [cityState, d.zip].filter(Boolean).join(" ") || d.address || "";
}

export default function Platform() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [dealers, setDealers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [detecting, setDetecting] = useState(null);
  const [q, setQ] = useState("");

  const isStaff = user?.platform_owner || user?.platform_admin;

  const load = (term = q) => {
    Promise.all([api.get(`/platform/dealers?q=${encodeURIComponent(term)}`), api.get("/platform/stats")])
      .then(([d, s]) => { setDealers(d.data); setStats(s.data); })
      .catch(() => toast.error("Failed to load platform data"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (!isStaff) return;
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, isStaff]);

  const setStatus = async (d, status) => {
    setBusy(d.id);
    try {
      await api.put(`/platform/dealers/${d.id}`, { status });
      toast.success(`${d.name} is now ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const setAddon = async (d, specialist_match) => {
    setBusy(d.id);
    try {
      await api.put(`/platform/dealers/${d.id}/addons`, { specialist_match });
      toast.success(specialist_match === "active" ? `Specialist Match activated for ${d.name}` : `Specialist Match turned off for ${d.name}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const setListing = async (d, listed) => {
    setBusy(d.id);
    try {
      const { data } = await api.put(`/platform/dealers/${d.id}/listing`, { listed });
      toast.success(listed ? `${d.name} now appears on the public site` : `${d.name} is hidden from the public site`);
      setDealers((prev) => prev.map((x) => (x.id === d.id ? data : x)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Permanently remove ${d.name} and all its logins, vehicles and leads?`)) return;
    setBusy(d.id);
    try {
      await api.delete(`/platform/dealers/${d.id}`);
      toast.success(`${d.name} removed`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const detectPlatform = async (d) => {
    setDetecting(d.id);
    try {
      const { data } = await api.post(`/platform/dealers/${d.id}/detect-platform`);
      const p = PLATFORMS[data.inventory_platform] || PLATFORMS.none;
      toast.success(`${d.name} runs ${p.label}${data.inventory_platform_detail ? ` · ${data.inventory_platform_detail}` : ""}`);
      setDealers((prev) => prev.map((x) => (x.id === d.id ? data : x)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDetecting(null);
    }
  };

  const enter = async (d) => {
    try {
      await api.post(`/platform/dealers/${d.id}/enter`);
      await refresh();
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!isStaff) {
    return <p className="text-slate-400" data-testid="platform-owner-only">Only Vinconex staff can view this page.</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Platform <span className="text-base font-semibold text-slate-500 align-middle" data-testid="platform-owner-line">· {BRAND.owner}</span></h1>
          <p className="text-sm text-slate-400 mt-1">Every dealership on Vinconex. Search by city, state or ZIP, approve sign-ups, or step into any dashboard. A green platform badge means that website has a full inventory feed; red badges and “Sync failing” need a look. Nobody is named on the public site until you press <b className="text-slate-200">Show on site</b> — their own <span className="font-mono-tech">/d/</span> page always works either way.</p>
        </div>
        <VinconexLogo variant="wordmark" className="h-5 w-auto hidden sm:block" to={null} />
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-3 mb-6" data-testid="platform-stats">
          <Stat label="Dealerships" value={stats.dealers} />
          <Stat label="Active" value={stats.active} />
          <Stat label="Pending" value={stats.pending} />
          <Stat label="Vehicles live" value={stats.vehicles} />
          <Stat label="Leads captured" value={stats.leads} />
          <Stat label="Add-on requests" value={stats.addon_requests ?? 0} />
          <Stat label="Shopper match requests" value={stats.match_requests ?? 0} />
          <Stat label="Contact inbox · new" value={stats.contact_new ?? 0} testId="stat-contact-new" />
          <Stat label="Sync failing" value={stats.sync_failing ?? 0} testId="stat-sync-failing" alert={(stats.sync_failing ?? 0) > 0} />
        </div>
      )}

      <DealerOnboardingPanel dealers={dealers} busy={busy} onApprove={(d) => setStatus(d, "active")} onEnter={enter} onChanged={() => load()} />

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input
          data-testid="platform-dealer-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search dealerships by name, city, state or ZIP…"
          className="pl-9 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#0A8CF2]" /></div>
      ) : (
        <div className="rounded-xl border border-[#2A2E3D] bg-[#12141C] divide-y divide-[#2A2E3D] mb-6" data-testid="platform-dealer-list">
          {dealers.map((d) => {
            const isCurrent = d.id === (user.active_dealer_id || user.dealer_id);
            const loc = locationOf(d);
            return (
              <div key={d.id} data-testid={`platform-dealer-${d.id}`} className="flex flex-col lg:flex-row lg:items-center gap-4 px-5 py-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {d.logo_url ? (
                    <img src={d.logo_url} alt="" className="h-9 w-9 rounded-md object-contain bg-black/40" />
                  ) : (
                    <span className="h-9 w-9 rounded-md flex items-center justify-center font-heading font-black text-white" style={{ background: d.accent_color }}>{d.name[0]}</span>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{d.name}</span>
                      <Badge variant="outline" data-testid={`platform-status-${d.id}`} className={`${STATUS[d.status] || ""} uppercase text-[10px] font-mono-tech`}>{d.status}</Badge>
                      <Badge variant="outline" data-testid={`platform-addon-${d.id}`} title="Specialist Match add-on" className={`${(ADDON_STATUS[d.addon_specialist_match] || ADDON_STATUS.none).cls} text-[10px] font-mono-tech flex items-center gap-1`}>
                        <Sparkles className="h-2.5 w-2.5" /> Match · {(ADDON_STATUS[d.addon_specialist_match] || ADDON_STATUS.none).label}
                      </Badge>
                      <PlatformBadge d={d} busy={detecting === d.id} onDetect={() => detectPlatform(d)} />
                      <SyncHealthBadge d={d} />
                      <Badge variant="outline" data-testid={`platform-listing-${d.id}`}
                             title={d.public_listed ? "Named in the dealer directory on vinconex.com" : "Not named anywhere public — their own /d/ page still works"}
                             className={`${d.public_listed ? "border-[#0A8CF2]/50 text-[#7FC3FF] bg-[#0A8CF2]/10" : "border-slate-700 text-slate-500"} text-[10px] font-mono-tech flex items-center gap-1`}>
                        {d.public_listed ? <Eye className="h-2.5 w-2.5" /> : <EyeOff className="h-2.5 w-2.5" />} {d.public_listed ? "On the site" : "Not listed"}
                      </Badge>
                      {isCurrent && <span className="text-[10px] text-slate-500 uppercase tracking-widest font-mono-tech">current</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                      {loc && <span className="inline-flex items-center gap-1" data-testid={`platform-location-${d.id}`}><MapPin className="h-3 w-3" /> {loc}</span>}
                      <a href={`/d/${d.slug}`} target="_blank" rel="noreferrer" className="hover:text-[#7FC3FF] inline-flex items-center gap-1">/d/{d.slug} <ExternalLink className="h-3 w-3" /></a>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-4 text-center text-sm lg:w-[440px]">
                  <div><div className="font-bold flex items-center justify-center gap-1"><Car className="h-3.5 w-3.5 text-slate-500" />{d.vehicles}</div><div className="text-[10px] text-slate-500 uppercase">vehicles</div></div>
                  <div><div className="font-bold flex items-center justify-center gap-1"><Inbox className="h-3.5 w-3.5 text-slate-500" />{d.leads}</div><div className="text-[10px] text-slate-500 uppercase">leads</div></div>
                  <div><div className="font-bold">{d.users}</div><div className="text-[10px] text-slate-500 uppercase">logins</div></div>
                  <div title={`${d.match_taps ?? 0} "Talk to a specialist" taps`}><div className={`font-bold flex items-center justify-center gap-1 ${(d.match_requests ?? 0) > 0 ? "text-amber-400" : ""}`} data-testid={`platform-match-requests-${d.id}`}><Headset className="h-3.5 w-3.5 text-slate-500" />{d.match_requests ?? 0}</div><div className="text-[10px] text-slate-500 uppercase">match req.</div></div>
                  <div><div className="font-bold text-xs pt-1">{d.last_synced_at ? new Date(d.last_synced_at).toLocaleDateString() : "—"}</div><div className="text-[10px] text-slate-500 uppercase">synced</div></div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => setListing(d, !d.public_listed)} disabled={busy === d.id}
                          data-testid={`platform-listing-toggle-${d.id}`}
                          title={d.public_listed ? "Stop naming them on the public site" : "Name them in the public dealer directory"}
                          className={`h-9 ${d.public_listed ? "border-[#0A8CF2]/50 bg-[#0A8CF2]/10 hover:bg-[#0A8CF2]/25 text-[#7FC3FF]" : "border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-300"}`}>
                    {d.public_listed ? <EyeOff className="mr-1.5 h-3.5 w-3.5" /> : <Eye className="mr-1.5 h-3.5 w-3.5" />}
                    {d.public_listed ? "Hide from site" : "Show on site"}
                  </Button>
                  {d.addon_specialist_match !== "active" ? (
                    <Button size="sm" onClick={() => setAddon(d, "active")} disabled={busy === d.id} data-testid={`platform-addon-activate-${d.id}`} className={`h-9 font-semibold ${d.addon_specialist_match === "requested" ? "bg-amber-500 hover:bg-amber-400 text-black" : "bg-slate-800 hover:bg-slate-700 text-slate-100"}`}>
                      <Sparkles className="mr-1.5 h-3.5 w-3.5" /> {d.addon_specialist_match === "requested" ? "Approve Specialist Match" : "Enable Specialist Match"}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setAddon(d, "none")} disabled={busy === d.id} data-testid={`platform-addon-disable-${d.id}`} className="h-9 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-300">
                      <Lock className="mr-1.5 h-3.5 w-3.5" /> Turn off Match
                    </Button>
                  )}
                  {d.status !== "active" && (
                    <Button size="sm" onClick={() => setStatus(d, "active")} disabled={busy === d.id} data-testid={`platform-approve-${d.id}`} className="h-9 bg-emerald-600 hover:bg-emerald-500 font-semibold">
                      <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> {d.status === "pending" ? "Approve" : d.status === "declined" ? "Approve anyway" : "Reactivate"}
                    </Button>
                  )}
                  {d.status === "active" && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(d, "suspended")} disabled={busy === d.id} data-testid={`platform-suspend-${d.id}`} className="h-9 border-red-900/50 bg-red-950/30 hover:bg-red-950/60 text-red-400">
                      <Ban className="mr-1.5 h-3.5 w-3.5" /> Suspend
                    </Button>
                  )}
                  {d.status === "suspended" && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(d, "pending")} disabled={busy === d.id} data-testid={`platform-pending-${d.id}`} className="h-9 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200">
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Set pending
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => enter(d)} disabled={isCurrent} data-testid={`platform-enter-${d.id}`} className="h-9 border-[#0A8CF2]/50 bg-[#0A8CF2]/10 hover:bg-[#0A8CF2]/25 text-[#7FC3FF]">
                    <LogIn className="mr-1.5 h-3.5 w-3.5" /> {isCurrent ? "Viewing" : "Enter dashboard"}
                  </Button>
                  {user.platform_owner && d.status !== "active" && d.id !== user.dealer_id && (
                    <Button size="sm" variant="outline" onClick={() => remove(d)} disabled={busy === d.id} data-testid={`platform-remove-${d.id}`} title="Remove dealership" className="h-9 border-slate-700 bg-slate-900/50 hover:bg-red-950/60 hover:text-red-400 text-slate-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          {dealers.length === 0 && (
            <div className="text-center py-16 text-slate-500" data-testid="platform-no-dealers">
              <Building2 className="h-8 w-8 mx-auto mb-2" /> {q ? `No dealerships match “${q}”.` : "No dealerships yet."}
            </div>
          )}
        </div>
      )}

      <SecurityPanel />
      <AuditLogPanel staff />
      <ContactInboxPanel />
      <DemandNudgePanel />
      <PlatformAdminsPanel />
    </div>
  );
}
